<!-- /src/views/Dashboard/transitions/Administrator.vue  -->
<template>
  <div class="admin-dashboard">
    <admin-stats />

    <!-- Recent Orders Section -->
    <div class="card shadow-sm mb-4">
      <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
        <div>
          <h5 class="mb-1 font-weight-bold">{{ $t("sentence.recentOrders") }}</h5>
          <small class="text-muted">{{ $t("sentence.recentOrders") }}</small>
        </div>
        <button class="btn btn-outline-primary btn-sm">
          {{ $t("action.viewAll") }}
          <i class="mdi mdi-arrow-right ml-2"></i>
        </button>
      </div>

      <div v-if="newOrders && newOrders.length" class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0">
            <thead class="thead-light">
              <tr>
                <th class="border-top-0">{{ $t("ecommerce.order") }} ID</th>
                <th class="border-top-0">{{ $t("attr.name") }}</th>
                <th class="border-top-0 d-none d-md-table-cell">{{ $t("attr.pictures") }}</th>
                <th class="border-top-0">{{ $t("main.status") }}</th>
                <th class="border-top-0">{{ $t("order.buyer") }}</th>
                <th class="border-top-0 d-none d-lg-table-cell">{{ $t("attr.date") }}</th>
                <th class="border-top-0">{{ $t("main.action") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in newOrders" :key="order.id">
                <td>
                  <span class="font-weight-bold text-primary">#{{ order.id }}</span>
                </td>
                <td>
                  <span class="text-truncate d-inline-block" style="max-width: 300px;">
                    <template v-if="order.products && order.products.length">
                      <span v-for="(p, idx) in order.products" :key="p.id">
                        {{ p.name | truncate(40) }}<span v-if="idx < order.products.length - 1">, </span>
                      </span>
                    </template>
                    <template v-else-if="order.product">
                      {{ order.product.name | truncate(40) }}
                    </template>
                    <template v-else>
                      N/A
                    </template>
                  </span>
                </td>
                <td class="d-none d-md-table-cell">
                  <div class="d-flex align-items-center">
                    <template v-if="order.products && order.products.length">
                      <img
                        v-for="p in order.products.slice(0, 3)"
                        :key="p.id"
                        :src="getImageUrl(p.pictures)"
                        class="admin-product-thumb mr-1"
                        :alt="p.name"
                        @click="goToProductDetailPage(p.id)"
                        style="cursor: pointer;"
                      />
                      <span v-if="order.products.length > 3" class="ml-2 text-muted small">+{{ order.products.length - 3 }}</span>
                    </template>
                    <template v-else-if="order.product">
                      <img :src="getImageUrl(order.product.pictures)" class="admin-product-thumb" alt="Product" />
                    </template>
                  </div>
                </td>
                <td>
                  <span :class="['badge', 'badge-pill', getStatusBadgeClass(order.status)]">
                    {{ order.status }}
                  </span>
                </td>
                <td class="text-capitalize">
                  {{ order.order_information.delivery_firstname }} 
                  {{ order.order_information.delivery_lastname }}
                </td>
                <td class="d-none d-lg-table-cell">
                  <small class="text-muted">{{ order.created_at | moment }}</small>
                </td>
                <td>
                  <button
                    @click="goToProductDetailPage(firstProductId(order))"
                    class="btn btn-sm btn-primary rounded-circle admin-action-btn"
                    :title="$t('action.viewDetails') || 'View details'"
                    :disabled="!firstProductId(order)"
                  >
                    <i class="mdi mdi-arrow-right"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-else class="card-body text-center py-5">
        <i class="mdi mdi-package-variant display-4 text-muted d-block mb-3"></i>
        <p class="text-muted mb-0">{{ $t("sentence.noNewOrders") }}</p>
      </div>
    </div>

    <!-- Two Column Layout -->
    <div class="row">
      <!-- New Customers Section -->
      <div class="col-lg-6 mb-4">
        <div class="card shadow-sm h-100">
          <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center py-3">
            <div class="d-flex align-items-center flex-wrap">
              <h5 class="mb-1 font-weight-bold mr-2">
                {{ $t("sentence.newCustomer") }}
              </h5>
              <span class="badge badge-warning">
                {{ $t("status.notYetValidated") }}
              </span>
            </div>
          </div>

          <div v-if="newUsers && newUsers.length" class="card-body p-2 admin-scrollable">
            <div class="list-group list-group-flush">
              <div 
                v-for="user in newUsers" 
                :key="user.id" 
                class="list-group-item list-group-item-action d-flex align-items-center px-3 py-3 border-0"
              >
                <div class="position-relative mr-3">
                  <img 
                    :src="user.picture ? storageUrl + user.picture : userpng" 
                    class="admin-avatar"
                    alt="" 
                  />
                  <span class="admin-status-dot"></span>
                </div>
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-items-center flex-wrap mb-1">
                    <span class="font-weight-semibold mr-2">
                      {{ user.firsname }} {{ user.lastname }}
                    </span>
                    <span :class="['badge', 'badge-sm', getRoleBadgeClass(user.roles[0])]">
                      {{ getRoleLabel(user.roles[0]) }}
                    </span>
                  </div>
                  <small class="text-muted d-flex align-items-center">
                    <i class="mdi mdi-email mr-1"></i>
                    <span class="text-truncate">{{ user.email || 'N/A' }}</span>
                  </small>
                </div>
                <router-link 
                  :to="{ name: 'manageUsers', params: { id: user.id } }"
                  class="btn btn-sm btn-primary rounded-circle admin-action-btn ml-2"
                >
                  <i class="mdi mdi-arrow-right"></i>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else class="card-body text-center py-5">
            <i class="mdi mdi-account-multiple display-4 text-muted d-block mb-3"></i>
            <p class="text-muted mb-0">{{ $t("sentence.noNewUsers") }}</p>
          </div>
        </div>
      </div>

      <!-- Latest Products Section -->
      <div class="col-lg-6 mb-4">
        <div class="card shadow-sm h-100">
          <div class="card-header bg-white border-bottom py-3">
            <h5 class="mb-1 font-weight-bold">{{ $t("ecommerce.product.latestProduct") }}</h5>
            <small class="text-muted">Recently added products</small>
          </div>

          <div v-if="newProducts && newProducts.length" class="card-body p-2 admin-scrollable">
            <div class="list-group list-group-flush">
              <div 
                v-for="product in newProducts" 
                :key="product.id" 
                class="list-group-item list-group-item-action d-flex align-items-start px-3 py-3 border-0"
              >
                <img 
                  :src="getImageUrl(product.pictures)" 
                  alt="Product" 
                  class="admin-product-img mr-3" 
                />
                <div class="flex-grow-1 min-width-0">
                  <div class="d-flex align-items-center flex-wrap mb-2">
                    <a href="#" class="font-weight-semibold text-dark mr-2">
                      {{ product.name }}
                    </a>
                    <span class="badge badge-warning badge-sm">
                      {{ $t("status.notYetValidated") }}
                    </span>
                  </div>
                  <p class="text-muted small mb-0 admin-text-clamp">
                    {{ product.description | truncate(100) }}
                  </p>
                </div>
                <router-link 
                  :to="{ name: 'listNewProduct', params: { id: product.id } }"
                  class="btn btn-sm btn-primary rounded-circle admin-action-btn ml-2"
                >
                  <i class="mdi mdi-arrow-right"></i>
                </router-link>
              </div>
            </div>
          </div>
          <div v-else class="card-body text-center py-5">
            <i class="mdi mdi-shopping display-4 text-muted d-block mb-3"></i>
            <p class="text-muted mb-0">{{ $t("sentence.noNewProducts") }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from "@/config.js";
import userpng from '@/assets/img/user/user.webp';
import AdminStats from '@/components/statistic/AdminStats.vue';

export default {
  name: "Administrator",
  components: { AdminStats },

  data() {
    return {
      storageUrl: config.storageUrl,
      userpng: userpng,
      newUsers: null,
      newOrders: null,
      newProducts: null,
    };
  },

  mounted() {
    this.initialLoad();
  },

  methods: {
    initialLoad() {
      this.getUserWaitingForValidation();
      this.getRecentOrder();
      this.getNewProducts();
    },

    getImageUrl(pictures) {
      if (Array.isArray(pictures) && pictures.length > 0) {
        return this.storageUrl + pictures[0];
      } else if (typeof pictures === 'string') {
        return this.storageUrl + pictures;
      }
      return '/default-product.png';
    },

    getRoleLabel(role) {
      return role === 'ROLE_SELLER' 
        ? this.$t('actor.pro') 
        : this.$t('actor.individual');
    },

    getRoleBadgeClass(role) {
      return role === 'ROLE_SELLER' ? 'badge-info' : 'badge-secondary';
    },

    getStatusBadgeClass(status) {
      const statusMap = {
        'Pending': 'badge-warning',
        'Processing': 'badge-info',
        'Completed': 'badge-success',
        'Cancelled': 'badge-danger',
      };
      return statusMap[status] || 'badge-primary';
    },

    async getUserWaitingForValidation() {
      try {
        const response = await this.$axios.get("admin/user/waiting");
        this.newUsers = response.data;
      } catch (err) {
        console.error("Error fetching waiting users:", err);
      }
    },

    async getRecentOrder() {
      try {
        const response = await this.$axios.get("admin/orders/list");
        this.newOrders = response.data;
      } catch (err) {
        console.error("Error fetching recent orders:", err);
      }
    },

    goToProductDetailPage(id) {
      if (!id) return;
      this.$router.push({ name: "productDetails", params: { id } });
    },

    firstProductId(order) {
      if (!order) return null;
      if (order.products && order.products.length) return order.products[0].id;
      if (order.product) return order.product.id;
      return null;
    },

    async getNewProducts() {
      try {
        const response = await this.$axios.get("admin/product/getNonValidated");
        this.newProducts = response.data;
      } catch (err) {
        console.error("Error fetching new products:", err);
      }
    },
  },
};
</script>

<style scoped>
/* Custom component-specific styles only - no Bootstrap overrides */
.admin-dashboard {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Custom admin-specific utilities */
.admin-product-thumb {
  width: 48px;
  height: 48px;
  border-radius: 0.25rem;
  object-fit: cover;
  border: 2px solid var(--gray-100);
}

.admin-product-img {
  width: 80px;
  height: 80px;
  border-radius: 0.25rem;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--gray-100);
}

.admin-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.admin-status-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  background-color: var(--success);
  border: 2px solid white;
  border-radius: 50%;
}



.admin-scrollable {
  max-height: 500px;
  overflow-y: auto;
}

.admin-scrollable::-webkit-scrollbar {
  width: 6px;
}

.admin-scrollable::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.admin-scrollable::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.admin-scrollable::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.admin-text-clamp {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.min-width-0 {
  min-width: 0;
}

.font-weight-semibold {
  font-weight: 600;
}

/* Smooth transitions for hover effects */
.list-group-item-action {
  transition: background-color 0.15s ease-in-out;
}

.card {
  transition: box-shadow 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .admin-dashboard {
    padding: 1rem;
  }
}

@media (max-width: 767.98px) {
  .admin-dashboard {
    padding: 0.75rem;
  }
  
  .admin-avatar {
    width: 44px;
    height: 44px;
  }
  
  .admin-product-img {
    width: 64px;
    height: 64px;
  }
}

@media (max-width: 575.98px) {
  .admin-dashboard {
    padding: 0.5rem;
  }
}
</style>